// frontend/src/form-editor/RichContentManager.js
import React, { useState, useEffect, useRef } from 'react';
import { API_BASE } from '../services/api.js';
import './RichContentManager.css';


const RichContentManager = ({ node, onBack }) => {

  const [blocks, setBlocks]               = useState([]);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState(null);
  const [success, setSuccess]             = useState(null);
  const [activeForm, setActiveForm]       = useState(null); // 'text'|'table'|'numerical'|'image'|'flowchart'|null
  const [editingBlock, setEditingBlock]   = useState(null); // block being edited
  const [language, setLanguage]           = useState('en');
  const [groupLabel, setGroupLabel]       = useState('');
    

 
  // ── Determine parent IDs from node ────────────────────────────────────────
  const parentIds = {
    micro_chunk_id : node.node_type === 'micro_chunk'  ? node.id : null,
    explanation_id : node.node_type === 'explanation'  ? node.id : null,
    subject_id     : node.subject_id  || null,
    chapter_id     : node.chapter_id  || null,
    topic_id       : node.topic_id    || null,
    subtopic_id    : node.subtopic_id || null,
    chunk_id       : node.chunk_id    || null,
  };

  // ── Load blocks on mount / language change ─────────────────────────────────
  useEffect(() => {
    loadBlocks();
  }, [node.id, language]);

  const loadBlocks = async () => {
    setLoading(true);
    setError(null);
    try {
      let url = '';
      if (node.node_type === 'micro_chunk') {
        url = `${API_BASE}/rich-content/micro-chunk/${node.id}?language=${language}`;
      } else {
        url = `${API_BASE}/rich-content/explanation/${node.id}?language=${language}`;
      }
      const res = await fetch(url);
      if (!res.ok) throw new Error('Failed to load rich content');
      const data = await res.json();
      setBlocks(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Save block (create or update) ─────────────────────────────────────────
  const saveBlock = async (blockData) => {
    setLoading(true);
    setError(null);
    try {
      let url, method;

      if (editingBlock) {
        // UPDATE existing block
        url    = `${API_BASE}/rich-content/${editingBlock.rich_content_id}`;
        method = 'PUT';
      } else {
        // CREATE new block
        url    = `${API_BASE}/rich-content/`;
        method = 'POST';
      }

      const payload = editingBlock
          ? {
              block_type   : blockData.block_type,
              content_data : blockData.content_data,
              order_index  : blockData.order_index ?? editingBlock.order_index,
            }
          : {
              ...parentIds,
              block_type   : blockData.block_type,
              content_data : blockData.content_data,
              order_index  : blocks.length + 1,
              language,
              group_label  : groupLabel || null,
            };

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Save failed');
      }

      setSuccess(editingBlock ? 'Block updated!' : 'Block added!');
      setTimeout(() => setSuccess(null), 2000);
      setActiveForm(null);
      setEditingBlock(null);
      await loadBlocks();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Delete block ───────────────────────────────────────────────────────────
  const deleteBlock = async (richContentId) => {
    if (!window.confirm('Delete this block?')) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/rich-content/${richContentId}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Delete failed');
      setSuccess('Block deleted');
      setTimeout(() => setSuccess(null), 2000);
      await loadBlocks();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Move block up / down ───────────────────────────────────────────────────
  const moveBlock = async (index, direction) => {
    const newBlocks = [...blocks];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= newBlocks.length) return;

    // Swap in local array
    [newBlocks[index], newBlocks[swapIndex]] = [newBlocks[swapIndex], newBlocks[index]];

    // Build ordered id list
    const orderedIds = newBlocks.map(b => b.rich_content_id);

    try {
      const endpoint = node.node_type === 'micro_chunk'
        ? `${API_BASE}/rich-content/reorder/micro-chunk/${node.id}`
        : `${API_BASE}/rich-content/reorder/micro-chunk/${node.id}`; // same endpoint for now

      const res = await fetch(endpoint, {
        method  : 'PUT',
        headers : { 'Content-Type': 'application/json' },
        body    : JSON.stringify(orderedIds),
      });
      if (!res.ok) throw new Error('Reorder failed');
      await loadBlocks();
    } catch (err) {
      setError(err.message);
    }
  };

  // ── Open edit form for existing block ─────────────────────────────────────
  const openEditBlock = (block) => {
    setEditingBlock(block);
    setActiveForm(block.block_type);
  };

  // ── Cancel form ───────────────────────────────────────────────────────────
  const cancelForm = () => {
    setActiveForm(null);
    setEditingBlock(null);
    setError(null);
  };

  // ── Render block preview ──────────────────────────────────────────────────
  const renderBlockPreview = (block, index) => {
    const typeLabels = {
      text      : '📝 Text',
      table     : '📊 Table',
      numerical : '🔢 Numerical',
      image     : '🖼️ Image',
      flowchart : '📐 Flowchart',
      html      : '🖥️ HTML',
      video     : '▶️ Video',
    };

    return (
      <div key={block.rich_content_id} className="rcm-block">

        {/* Block header */}
        <div className="rcm-block-header">
          <span className="rcm-block-type">{typeLabels[block.block_type] || block.block_type}</span>
          <span className="rcm-block-order">#{index + 1}</span>
          {block.group_label && (
            <span className="rcm-block-group">{block.group_label}</span>
          )}
          <div className="rcm-block-actions">
            <button className="rcm-btn rcm-btn-sm" onClick={() => moveBlock(index, 'up')}   disabled={index === 0}>↑</button>
            <button className="rcm-btn rcm-btn-sm" onClick={() => moveBlock(index, 'down')} disabled={index === blocks.length - 1}>↓</button>
            <button className="rcm-btn rcm-btn-edit" onClick={() => openEditBlock(block)}>✏️ Edit</button>
            <button className="rcm-btn rcm-btn-delete" onClick={() => deleteBlock(block.rich_content_id)}>🗑️ Delete</button>
          </div>
        </div>

        {/* Block content preview */}
        <div className="rcm-block-preview">
          {block.block_type === 'text' && (
            <p>{block.content_data?.content?.substring(0, 120)}{block.content_data?.content?.length > 120 ? '...' : ''}</p>
          )}
          {block.block_type === 'table' && (
            <span className="rcm-preview-meta">
              {block.content_data?.columns?.length || 0} columns × {block.content_data?.rows?.length || 0} rows
              &nbsp;|&nbsp; Style: {block.content_data?.table_style || 'default'}
            </span>
          )}
          {block.block_type === 'numerical' && (
            <p>{block.content_data?.question?.substring(0, 120)}{block.content_data?.question?.length > 120 ? '...' : ''}</p>
          )}
          {block.block_type === 'image' && (
            <span className="rcm-preview-meta">📎 {block.content_data?.file_path || 'No file'}</span>
          )}
          {block.block_type === 'flowchart' && (
            <span className="rcm-preview-meta">Mermaid diagram · {block.content_data?.caption || 'No caption'}</span>
          )}

          {block.block_type === 'html' && (
            <span className="rcm-preview-meta">🖥️ Raw HTML block · {block.content_data?.html?.length || 0} characters</span>
          )}
          {block.block_type === 'video' && (
            <span className="rcm-preview-meta">
              ▶️ {block.content_data?.video_type === 'short' ? 'YouTube Short' : 'YouTube Video'} · ID: {block.content_data?.youtube_id}
            </span>
          )}
        </div>

      </div>
    );
  };

  // ==========================================================================
  // FORMS
  // ==========================================================================

  // ── TEXT FORM ──────────────────────────────────────────────────────────────
  const TextForm = ({ initial, onSave, onCancel }) => {
    const [content, setContent] = useState(initial?.content_data?.content || '');
    return (
      <div className="rcm-form">
        <h4>📝 {initial ? 'Edit' : 'Add'} Text Block</h4>
        <textarea
          className="rcm-textarea"
          rows={5}
          value={content}
          onChange={e => setContent(e.target.value)}
          placeholder="Enter text paragraph..."
        />
        <div className="rcm-form-actions">
          <button className="rcm-btn rcm-btn-secondary" onClick={onCancel}>Cancel</button>
          <button
            className="rcm-btn rcm-btn-primary"
            onClick={() => onSave({ block_type: 'text', content_data: { content } })}
            disabled={!content.trim()}
          >
            Save Text
          </button>
        </div>
      </div>
    );
  };

  // ── TABLE FORM ─────────────────────────────────────────────────────────────
  const TableForm = ({ initial, onSave, onCancel }) => {

    const initColumns = initial?.content_data?.columns || [
      { header: 'Column 1', width: '25%' },
      { header: 'Column 2', width: '25%' },
      { header: 'Column 3', width: '25%' },
      { header: 'Column 4', width: '25%' },
    ];
    const initRows = initial?.content_data?.rows || [['', '', '', '']];

    const [columns,    setColumns]    = useState(initColumns);
    const [rows,       setRows]       = useState(initRows);
    const [hasHeader,  setHasHeader]  = useState(initial?.content_data?.has_header ?? true);
    const [tableStyle, setTableStyle] = useState(initial?.content_data?.table_style || 'default');
    const [title, setTitle] = useState(initial?.content_data?.title || '');
    const pasteRef = useRef(null);

    // ── Column operations ──────────────────────────────────────────────────
    const addColumn = () => {
      setColumns(prev => [...prev, { header: `Column ${prev.length + 1}`, width: 'auto' }]);
      setRows(prev => prev.map(row => [...row, '']));
    };

    const removeColumn = (colIndex) => {
      if (columns.length <= 1) return;
      setColumns(prev => prev.filter((_, i) => i !== colIndex));
      setRows(prev => prev.map(row => row.filter((_, i) => i !== colIndex)));
    };

    const updateColumnHeader = (colIndex, value) => {
      setColumns(prev => prev.map((col, i) => i === colIndex ? { ...col, header: value } : col));
    };

    const updateColumnWidth = (colIndex, value) => {
      setColumns(prev => prev.map((col, i) => i === colIndex ? { ...col, width: value } : col));
    };

    // ── Row operations ─────────────────────────────────────────────────────
    const addRow = () => {
      setRows(prev => [...prev, Array(columns.length).fill('')]);
    };

    const removeRow = (rowIndex) => {
      if (rows.length <= 1) return;
      setRows(prev => prev.filter((_, i) => i !== rowIndex));
    };

    const updateCell = (rowIndex, colIndex, value) => {
      setRows(prev => prev.map((row, ri) =>
        ri === rowIndex ? row.map((cell, ci) => ci === colIndex ? value : cell) : row
      ));
    };

    // ── Paste from Excel ───────────────────────────────────────────────────
    const handlePaste = (e) => {
      e.preventDefault();
      const text = e.clipboardData.getData('text/plain');
      const pastedRows = text.trim().split('\n').map(row => row.split('\t'));

      if (pastedRows.length === 0) return;

      // If first paste and no data yet, use first row as headers
      const firstRow   = pastedRows[0];
      const dataRows   = pastedRows.slice(1);

      // Adjust columns to match pasted data
      const newColumns = firstRow.map((header, i) => ({
        header : header.trim(),
        width  : `${Math.floor(100 / firstRow.length)}%`,
      }));

      const newRows = dataRows.map(row => {
        // Pad or trim row to match column count
        const padded = [...row];
        while (padded.length < newColumns.length) padded.push('');
        return padded.slice(0, newColumns.length).map(c => c.trim());
      });

      setColumns(newColumns);
      setRows(newRows.length > 0 ? newRows : [Array(newColumns.length).fill('')]);
    };

    const handleSave = () => {
        onSave({
            block_type   : 'table',
            content_data : { title, columns, rows, has_header: hasHeader, table_style: tableStyle },
        });
    };

    return (
      <div className="rcm-form rcm-table-form">
        <h4>📊 {initial ? 'Edit' : 'Add'} Table</h4>

        {/* Table options */}
        <div className="rcm-table-options">
          <label>
            <input type="checkbox" checked={hasHeader} onChange={e => setHasHeader(e.target.checked)} />
            &nbsp;First row is header
          </label>
          <label>
            Style:&nbsp;
            <select value={tableStyle} onChange={e => setTableStyle(e.target.value)} className="rcm-select">
              <option value="default">Default</option>
              <option value="ledger">Ledger (Accounts)</option>
              <option value="comparison">Comparison</option>
              <option value="highlight">Highlight</option>
            </select>
          </label>
        </div>

        {/* Title */}
        <div className="rcm-form-field">
        <label className="rcm-label">Table Title (optional)</label>
        <input
            className="rcm-input"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="e.g. Trading Account for the year ended 31st March 2024"
        />
        </div>
        
        
        
        {/* Paste area */}
        <div
          className="rcm-paste-area"
          onPaste={handlePaste}
          tabIndex={0}
          ref={pasteRef}
        >
          📋 Click here and paste Excel data (Ctrl+V) — or build manually below
        </div>

        {/* Column headers + widths */}
        <div className="rcm-col-headers">
          <span className="rcm-col-label">Columns:</span>
          {columns.map((col, ci) => (
            <div key={ci} className="rcm-col-header-item">
              <input
                className="rcm-input rcm-col-name"
                value={col.header}
                onChange={e => updateColumnHeader(ci, e.target.value)}
                placeholder={`Col ${ci + 1}`}
              />
              <input
                className="rcm-input rcm-col-width"
                value={col.width}
                onChange={e => updateColumnWidth(ci, e.target.value)}
                placeholder="25%"
                title="Column width (e.g. 25%)"
              />
              <button className="rcm-btn rcm-btn-danger rcm-btn-xs" onClick={() => removeColumn(ci)} title="Remove column">×</button>
            </div>
          ))}
          <button className="rcm-btn rcm-btn-sm" onClick={addColumn}>+ Col</button>
        </div>

        {/* Table grid */}
        <div className="rcm-table-grid-wrapper">
          <table className="rcm-table-grid">
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri}>
                  <td className="rcm-row-num">{ri + 1}</td>
                  {row.map((cell, ci) => (
                    <td key={ci}>
                      <input
                        className="rcm-cell-input"
                        value={cell}
                        onChange={e => updateCell(ri, ci, e.target.value)}
                      />
                    </td>
                  ))}
                  <td>
                    <button className="rcm-btn rcm-btn-danger rcm-btn-xs" onClick={() => removeRow(ri)} title="Remove row">×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <button className="rcm-btn rcm-btn-sm rcm-add-row-btn" onClick={addRow}>+ Add Row</button>

        <div className="rcm-form-actions">
          <button className="rcm-btn rcm-btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="rcm-btn rcm-btn-primary"   onClick={handleSave}>Save Table</button>
        </div>
      </div>
    );
  };

  // ── NUMERICAL FORM ─────────────────────────────────────────────────────────
  const NumericalForm = ({ initial, onSave, onCancel }) => {
    const [question,     setQuestion]     = useState(initial?.content_data?.question      || '');
    const [given,        setGiven]        = useState(initial?.content_data?.given         || ['']);
    const [steps,        setSteps]        = useState(initial?.content_data?.steps         || [{ label: 'Step 1', content: '' }]);
    const [answer,       setAnswer]       = useState(initial?.content_data?.answer        || '');
    const [givenLabel,   setGivenLabel]   = useState(initial?.content_data?.given_label   || 'Given');
    const [answerLabel,  setAnswerLabel]  = useState(initial?.content_data?.answer_label  || 'Answer');

    // Given items
    const addGiven    = ()           => setGiven(prev => [...prev, '']);
    const removeGiven = (i)          => setGiven(prev => prev.filter((_, idx) => idx !== i));
    const updateGiven = (i, val)     => setGiven(prev => prev.map((g, idx) => idx === i ? val : g));

    // Steps
    const addStep    = ()            => setSteps(prev => [...prev, { label: `Step ${prev.length + 1}`, content: '' }]);
    const removeStep = (i)           => setSteps(prev => prev.filter((_, idx) => idx !== i));
    const updateStep = (i, key, val) => setSteps(prev => prev.map((s, idx) => idx === i ? { ...s, [key]: val } : s));

    const handleSave = () => {
      onSave({
        block_type   : 'numerical',
        content_data : {
          question,
          given        : given.filter(g => g.trim()),
          given_label  : givenLabel,
          steps        : steps.filter(s => s.content.trim()),
          answer,
          answer_label : answerLabel,
        },
      });
    };

    return (
      <div className="rcm-form rcm-numerical-form">
        <h4>🔢 {initial ? 'Edit' : 'Add'} Numerical Q&A</h4>

        {/* Question */}
        <div className="rcm-form-field">
          <label className="rcm-label">Question *</label>
          <textarea
            className="rcm-textarea"
            rows={3}
            value={question}
            onChange={e => setQuestion(e.target.value)}
            placeholder="Enter the numerical question..."
          />
        </div>

        {/* Labels customization */}
        <div className="rcm-form-field rcm-labels-row">
          <div>
            <label className="rcm-label">Given Section Label</label>
            <input
              className="rcm-input"
              value={givenLabel}
              onChange={e => setGivenLabel(e.target.value)}
              placeholder="e.g. Given, Known Data, Information"
            />
          </div>
          <div>
            <label className="rcm-label">Answer Section Label</label>
            <input
              className="rcm-input"
              value={answerLabel}
              onChange={e => setAnswerLabel(e.target.value)}
              placeholder="e.g. Answer, Result, Conclusion"
            />
          </div>
        </div>
        
        
        
        {/* Given */}
        <div className="rcm-form-field">
          <label className="rcm-label">Given Data</label>
          {given.map((g, i) => (
            <div key={i} className="rcm-step-row">
              <input
                className="rcm-input rcm-step-input"
                value={g}
                onChange={e => updateGiven(i, e.target.value)}
                placeholder={`e.g. Sales = ₹5,00,000`}
              />
              <button className="rcm-btn rcm-btn-danger rcm-btn-xs" onClick={() => removeGiven(i)}>×</button>
            </div>
          ))}
          <button className="rcm-btn rcm-btn-sm" onClick={addGiven}>+ Add Given Item</button>
        </div>

        {/* Steps */}
        <div className="rcm-form-field">
          <label className="rcm-label">Solution Steps</label>
          {steps.map((step, i) => (
            <div key={i} className="rcm-step-row">
              <input
                className="rcm-input rcm-step-label"
                value={step.label}
                onChange={e => updateStep(i, 'label', e.target.value)}
                placeholder="Step label"
              />
              <textarea
                className="rcm-textarea rcm-step-content"
                rows={2}
                value={step.content}
                onChange={e => updateStep(i, 'content', e.target.value)}
                placeholder="Step explanation..."
              />
              <button className="rcm-btn rcm-btn-danger rcm-btn-xs" onClick={() => removeStep(i)}>×</button>
            </div>
          ))}
          <button className="rcm-btn rcm-btn-sm" onClick={addStep}>+ Add Step</button>
        </div>

        {/* Answer */}
        <div className="rcm-form-field">
          <label className="rcm-label">Final Answer *</label>
          <input
            className="rcm-input"
            value={answer}
            onChange={e => setAnswer(e.target.value)}
            placeholder="e.g. Gross Profit = ₹1,80,000"
          />
        </div>

        <div className="rcm-form-actions">
          <button className="rcm-btn rcm-btn-secondary" onClick={onCancel}>Cancel</button>
          <button
            className="rcm-btn rcm-btn-primary"
            onClick={handleSave}
            disabled={!question.trim()}
          >
            Save Numerical
          </button>
        </div>
      </div>
    );
  };

  // ── IMAGE FORM ─────────────────────────────────────────────────────────────
  const ImageForm = ({ initial, onSave, onCancel }) => {
    const [caption,  setCaption]  = useState(initial?.content_data?.caption   || '');
    const [filePath, setFilePath] = useState(initial?.content_data?.file_path || '');
    const [uploading, setUploading] = useState(false);

    const handleFileUpload = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(`${API_BASE}/rich-content/upload-image`, {
          method : 'POST',
          body   : formData,
        });
        if (!res.ok) throw new Error('Upload failed');
        const data = await res.json();
        setFilePath(data.file_path);
      } catch (err) {
        alert('Image upload failed: ' + err.message);
      } finally {
        setUploading(false);
      }
    };

    return (
      <div className="rcm-form">
        <h4>🖼️ {initial ? 'Edit' : 'Add'} Image</h4>
        <div className="rcm-form-field">
          <label className="rcm-label">Upload Image</label>
          <input type="file" accept="image/*" onChange={handleFileUpload} />
          {uploading && <span> Uploading...</span>}
          {filePath && <div className="rcm-image-preview"><img src={`${API_BASE}/${filePath}`} alt="preview" /></div>}
        </div>
        <div className="rcm-form-field">
          <label className="rcm-label">Caption (optional)</label>
          <input className="rcm-input" value={caption} onChange={e => setCaption(e.target.value)} placeholder="e.g. Figure 1: T-Account" />
        </div>
        <div className="rcm-form-actions">
          <button className="rcm-btn rcm-btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="rcm-btn rcm-btn-primary" onClick={() => onSave({ block_type: 'image', content_data: { file_path: filePath, caption } })} disabled={!filePath}>
            Save Image
          </button>
        </div>
      </div>
    );
  };

  // ── FLOWCHART FORM ─────────────────────────────────────────────────────────
  const FlowchartForm = ({ initial, onSave, onCancel }) => {
    const [mermaidCode, setMermaidCode] = useState(
      initial?.content_data?.mermaid_code ||
      'flowchart TD\n    A[Start] --> B[Step 1]\n    B --> C[Step 2]\n    C --> D[End]'
    );
    const [caption, setCaption] = useState(initial?.content_data?.caption || '');

    return (
      <div className="rcm-form">
        <h4>📐 {initial ? 'Edit' : 'Add'} Flowchart</h4>
        <div className="rcm-form-field">
          <label className="rcm-label">Mermaid Diagram Code</label>
          <textarea
            className="rcm-textarea rcm-mono"
            rows={8}
            value={mermaidCode}
            onChange={e => setMermaidCode(e.target.value)}
            placeholder="flowchart TD&#10;    A[Start] --> B[End]"
          />
          <small className="rcm-hint">
            Use Mermaid syntax. Example: flowchart TD / A[Label] --&gt; B[Label]
          </small>
        </div>
        <div className="rcm-form-field">
          <label className="rcm-label">Caption (optional)</label>
          <input className="rcm-input" value={caption} onChange={e => setCaption(e.target.value)} placeholder="e.g. Accounting Cycle" />
        </div>
        <div className="rcm-form-actions">
          <button className="rcm-btn rcm-btn-secondary" onClick={onCancel}>Cancel</button>
          <button
            className="rcm-btn rcm-btn-primary"
            onClick={() => onSave({ block_type: 'flowchart', content_data: { mermaid_code: mermaidCode, caption } })}
            disabled={!mermaidCode.trim()}
          >
            Save Flowchart
          </button>
        </div>
      </div>
    );
  };

  // ── HTML FORM ──────────────────────────────────────────────────────────────
  const HTMLForm = ({ initial, onSave, onCancel }) => {
    const [html, setHtml] = useState(initial?.content_data?.html || '');

    return (
      <div className="rcm-form">
        <h4>🖥️ {initial ? 'Edit' : 'Add'} Raw HTML Block</h4>
        <div className="rcm-form-field">
          <label className="rcm-label">Paste HTML Code</label>
          <textarea
            className="rcm-textarea rcm-mono"
            rows={12}
            value={html}
            onChange={e => setHtml(e.target.value)}
            placeholder="Paste your HTML code here..."
          />
          <small className="rcm-hint">
            Paste complete HTML including tables, styles and text. This will render as-is on the student screen.
          </small>
        </div>
        {html && (
          <div className="rcm-form-field">
            <label className="rcm-label">Preview</label>
            <iframe
              srcDoc={`<!DOCTYPE html><html><head><meta name="viewport" content="width=device-width, initial-scale=1.0"><style>* { box-sizing: border-box; } body { margin: 8px; font-family: Arial, sans-serif; font-size: 14px; } table { border-collapse: collapse; width: 100%; } th, td { border: 1px solid #ccc; padding: 8px; } img { max-width: 100%; height: auto; }</style></head><body>${html}</body></html>`}
              style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: '6px', minHeight: '150px' }}
              onLoad={(e) => {
                const iframe = e.target;
                const body = iframe.contentDocument?.body;
                if (body) iframe.style.height = body.scrollHeight + 20 + 'px';
              }}
              title="HTML Preview"
              sandbox="allow-same-origin"
            />
          </div>
        )}
        <div className="rcm-form-actions">
          <button className="rcm-btn rcm-btn-secondary" onClick={onCancel}>Cancel</button>
          <button
            className="rcm-btn rcm-btn-primary"
            onClick={() => onSave({ block_type: 'html', content_data: { html } })}
            disabled={!html.trim()}
          >
            Save HTML Block
          </button>
        </div>
      </div>
    );
  };

  // ── VIDEO FORM ─────────────────────────────────────────────────────────────
  const VideoForm = ({ initial, onSave, onCancel }) => {
    const [youtubeId,    setYoutubeId]    = useState(initial?.content_data?.youtube_id    || '');
    const [videoType,    setVideoType]    = useState(initial?.content_data?.video_type    || 'regular');
    const [caption,      setCaption]      = useState(initial?.content_data?.caption       || '');
    const [startSeconds, setStartSeconds] = useState(initial?.content_data?.start_seconds || 0);

    // Extract YouTube ID from full URL if pasted
    const handleYoutubeInput = (value) => {
      // Handle full URLs
      const patterns = [
        /youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
        /youtu\.be\/([a-zA-Z0-9_-]{11})/,
        /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
        /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
      ];
      for (const pattern of patterns) {
        const match = value.match(pattern);
        if (match) {
          setYoutubeId(match[1]);
          // Auto detect shorts
          if (value.includes('/shorts/')) setVideoType('short');
          return;
        }
      }
      // Plain ID entered directly
      setYoutubeId(value);
    };

    return (
      <div className="rcm-form">
        <h4>▶️ {initial ? 'Edit' : 'Add'} Video Block</h4>

        <div className="rcm-form-field">
          <label className="rcm-label">YouTube URL or Video ID *</label>
          <input
            className="rcm-input"
            value={youtubeId}
            onChange={e => handleYoutubeInput(e.target.value)}
            placeholder="Paste YouTube URL or video ID"
          />
          <small className="rcm-hint">
            Paste full URL (youtube.com/watch?v=... or youtu.be/... or youtube.com/shorts/...) or just the video ID
          </small>
        </div>

        <div className="rcm-form-field rcm-labels-row">
          <div>
            <label className="rcm-label">Video Type</label>
            <select
              className="rcm-select"
              value={videoType}
              onChange={e => setVideoType(e.target.value)}
            >
              <option value="regular">Regular Video (16:9)</option>
              <option value="short">YouTube Short (9:16)</option>
            </select>
          </div>
          <div>
            <label className="rcm-label">Start at (seconds)</label>
            <input
              className="rcm-input"
              type="number"
              min="0"
              value={startSeconds}
              onChange={e => setStartSeconds(parseInt(e.target.value) || 0)}
              placeholder="0"
            />
          </div>
        </div>

        <div className="rcm-form-field">
          <label className="rcm-label">Caption (optional)</label>
          <input
            className="rcm-input"
            value={caption}
            onChange={e => setCaption(e.target.value)}
            placeholder="e.g. Video explanation of Partnership Accounts"
          />
        </div>

        {/* Preview */}
        {youtubeId && (
          <div className="rcm-form-field">
            <label className="rcm-label">Preview</label>
            <div className={videoType === 'short' ? 'rcm-video-preview-short' : 'rcm-video-preview'}>
              <iframe
                src={`https://www.youtube.com/embed/${youtubeId}${startSeconds ? `?start=${startSeconds}` : ''}`}
                frameBorder="0"
                allowFullScreen
                title="Video preview"
              />
            </div>
          </div>
        )}

        <div className="rcm-form-actions">
          <button className="rcm-btn rcm-btn-secondary" onClick={onCancel}>Cancel</button>
          <button
            className="rcm-btn rcm-btn-primary"
            onClick={() => onSave({
              block_type   : 'video',
              content_data : { youtube_id: youtubeId, video_type: videoType, caption, start_seconds: startSeconds },
            })}
            disabled={!youtubeId.trim()}
          >
            Save Video
          </button>
        </div>
      </div>
    );
  };

  // ── Render active form ─────────────────────────────────────────────────────
  const renderActiveForm = () => {
    const props = {
      initial  : editingBlock,
      onSave   : saveBlock,
      onCancel : cancelForm,
    };
    switch (activeForm) {
      case 'text'      : return <TextForm      {...props} />;
      case 'table'     : return <TableForm     {...props} />;
      case 'numerical' : return <NumericalForm {...props} />;
      case 'image'     : return <ImageForm     {...props} />;
      case 'flowchart' : return <FlowchartForm {...props} />;
      case 'html'      : return <HTMLForm      {...props} />;
      case 'video'     : return <VideoForm     {...props} />;
      default          : return null;
    }
  };

  // ==========================================================================
  // MAIN RENDER
  // ==========================================================================
  return (
    <div className="rcm-container">

      {/* Header */}
      <div className="rcm-header">
        <button className="rcm-back-btn" onClick={onBack}>← Back to Node</button>
        <div className="rcm-header-info">
          <h3>📊 Rich Content Manager</h3>
          <span className="rcm-node-label">
            {node.node_type === 'micro_chunk' ? '🔍 Micro Chunk' : '💡 Explanation'}
            &nbsp;#{node.id}
          </span>
        </div>
        <select className="rcm-select" value={language} onChange={e => setLanguage(e.target.value)}>
          <option value="en">🇺🇸 English</option>
          <option value="hi">🇮🇳 Hindi</option>
        </select>
      </div>

      {/* Messages */}
      {error   && <div className="rcm-alert rcm-alert-error">❌ {error}</div>}
      {success && <div className="rcm-alert rcm-alert-success">✅ {success}</div>}

      {/* Add block buttons — only show when no form is active */}
      {!activeForm && (
        <div className="rcm-add-buttons">
          <div className="rcm-group-label-row">
            <span className="rcm-add-label">Group:</span>
            <input
              className="rcm-input"
              style={{ width: '200px' }}
              value={groupLabel}
              onChange={e => setGroupLabel(e.target.value)}
              placeholder="e.g. Example 1, Solution, Working Notes"
            />
          </div>
          <div className="rcm-add-btns-row">
            <span className="rcm-add-label">Add Block:</span>
            <button className="rcm-add-btn" onClick={() => setActiveForm('text')}>      📝 Text</button>
            <button className="rcm-add-btn" onClick={() => setActiveForm('table')}>     📊 Table</button>
            <button className="rcm-add-btn" onClick={() => setActiveForm('numerical')}> 🔢 Numerical</button>
            <button className="rcm-add-btn" onClick={() => setActiveForm('image')}>     🖼️ Image</button>
            <button className="rcm-add-btn" onClick={() => setActiveForm('flowchart')}> 📐 Flowchart</button>
            <button className="rcm-add-btn" onClick={() => setActiveForm('html')}>      🖥️ HTML</button>
            <button className="rcm-add-btn" onClick={() => setActiveForm('video')}>     ▶️ Video</button>
          </div>
        </div>
      )}

      {/* Active form */}
      {activeForm && renderActiveForm()}

      {/* Blocks list */}
      {!activeForm && (
        <div className="rcm-blocks-list">
          {loading && <div className="rcm-loading">Loading...</div>}

          {!loading && blocks.length === 0 && (
            <div className="rcm-empty">
              No rich content blocks yet. Use the buttons above to add content.
            </div>
          )}

          {!loading && blocks.map((block, index) => renderBlockPreview(block, index))}
        </div>
      )}

    </div>
  );
};

export default RichContentManager;